import * as Yup from "yup";

export const defaultSteps = [
  {
    id: "service",
    fields: ["serviceName"],
  },
  {
    id: "details",
    fields: [
      "userName",
      "userPhone",
      "plateType",
      "plateNum",
      "plateLetters",
      "carBrand",
      "carColor",
      "parkingSlotNum",
    ],
  },
  {
    id: "paymentType",
    fields: [
      "paymentType",
      "cardNumber",
      "name",
      "cvv",
      "expiry_at",
      "amexCardNumber",
      "amexName",
      "amexCvv",
      "amexExpiryAt",
    ],
  },
  {
    id: "requested",
    fields: [],
  },
  {
    id: "cancelled",
    fields: [],
  },
];

export interface FormData extends Yup.InferType<typeof schema> {}

// validations
export const schema = Yup.object().shape({
  // type step
  serviceName: Yup.string().required("Required"),
  // details step
  userName: Yup.string().required("Required"),
  countryCode: Yup.string().required("Required"),
  isoCode: Yup.string().required("Required"),
  userPhone: Yup.string()
    .required("Required")
    .when("countryCode", ([countryCode], schema) => {
      // Saudi Arabia (+966)
      if (countryCode === "+966" || countryCode === "966") {
        return schema.matches(
          /^[5٥][0-9٠-٩]{8}$/,
          "invalidPhoneNumber",
        );
      }

      // International
      return schema.matches(/^[0-9٠-٩]{8,}$/, "invalidPhoneNumber");
    }),
  plateType: Yup.string()
    .required("Required")
    .oneOf(["saudi", "non-saudi"] as const),
  plateLetters: Yup.string().required("Required"),
  plateNum: Yup.string().when("plateType", {
    is: "saudi",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  carBrand: Yup.string().required("Required"),
  carColor: Yup.string().required("Required"),
  parkingSlotNum: Yup.string().required("Required"),
  // Payment type
  paymentType: Yup.string()
    .required("Required")
    .oneOf(["card", "applepay", "googlepay", "cashier", "amex"] as const),
  // card
  name: Yup.string().when("paymentType", {
    is: "card",
    then: (value) => value.required("Required"),
  }),
  cardNumber: Yup.string().when("paymentType", {
    is: "card",
    then: (value) =>
      value
        .required("Required")
        .matches(/^\d{16}$/, "invalidCardNumberShouldBe16")
        .test("luhn", "invalidCardNumber", isValidLuhn),
  }),
  expiry_at: Yup.string().when("paymentType", {
    is: "card",
    then: (value) =>
      value
        .required("Required")
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "invalidFormatUseMM/YY"),
  }),
  cvv: Yup.string().when("paymentType", {
    is: "card",
    then: (value) =>
      value.required("Required").matches(/^\d{3,4}$/, "invalidCVV"),
  }),
  // AMEX
  amexName: Yup.string().when("paymentType", {
    is: "amex",
    then: (value) => value.required("Required"),
  }),
  amexCardNumber: Yup.string().when("paymentType", {
    is: "amex",
    then: (value) =>
      value
        .required("Required")
        .matches(/^\d{15}$/, "invalidCardNumberShouldBe16")
        .test("luhn", "invalidCardNumber", isValidLuhn),
  }),
  amexExpiryAt: Yup.string().when("paymentType", {
    is: "amex",
    then: (value) =>
      value
        .required("Required")
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "invalidFormatUseMM/YY"),
  }),
  amexCvv: Yup.string().when("paymentType", {
    is: "amex",
    then: (value) =>
      value.required("Required").matches(/^\d{3,4}$/, "invalidCVV"),
  }),
});

function isValidLuhn(value: string): boolean {
  // Implementation of the Luhn algorithm
  let sum = 0;
  let isEven = false;

  for (let i = value.length - 1; i >= 0; i--) {
    let digit = parseInt(value[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}
